// src/utils/auth.js

/**
 * Centralized authentication utilities
 * Handles token management and auth state
 */

import { STORAGE_KEYS } from './constants';

/**
 * Check if user is authenticated (any portal)
 */
export const isAuthenticated = () => {
    return (
        localStorage.getItem(STORAGE_KEYS.IS_AUTHED) === '1' ||
        sessionStorage.getItem(STORAGE_KEYS.IS_AUTHED) === '1'
    );
};

/**
 * Get the portal the user signed in to (admin, borrower, or lender)
 */
export const getPortal = () => {
    return (
        localStorage.getItem(STORAGE_KEYS.PORTAL) ||
        sessionStorage.getItem(STORAGE_KEYS.PORTAL) ||
        ''
    );
};

/**
 * Check if user is authenticated as admin (must have signed in via admin sign-in)
 */
export const isAdminAuthed = () => {
    return (
        (localStorage.getItem(STORAGE_KEYS.IS_AUTHED) === '1' || sessionStorage.getItem(STORAGE_KEYS.IS_AUTHED) === '1') &&
        (localStorage.getItem(STORAGE_KEYS.PORTAL) === 'admin' || sessionStorage.getItem(STORAGE_KEYS.PORTAL) === 'admin')
    );
};

/**
 * Check if user is authenticated for a specific portal
 */
export const isAuthedForPortal = (portal) => {
    if (!portal) return isAuthenticated();
    return isAuthenticated() && getPortal() === portal;
};

/**
 * Set authentication state
 */
export const setAuthState = (isAuthed, remember = false) => {
    if (isAuthed) {
        if (remember) {
            localStorage.setItem(STORAGE_KEYS.IS_AUTHED, '1');
        } else {
            sessionStorage.setItem(STORAGE_KEYS.IS_AUTHED, '1');
        }
    } else {
        localStorage.removeItem(STORAGE_KEYS.IS_AUTHED);
        sessionStorage.removeItem(STORAGE_KEYS.IS_AUTHED);
    }
};

/**
 * Set which portal the user is signed into (admin | borrower | lender).
 * Use the same storage as auth: remember = localStorage, else sessionStorage.
 */
export const setPortal = (portal, remember = false) => {
    if (portal) {
        if (remember) {
            localStorage.setItem(STORAGE_KEYS.PORTAL, portal);
            sessionStorage.removeItem(STORAGE_KEYS.PORTAL);
        } else {
            sessionStorage.setItem(STORAGE_KEYS.PORTAL, portal);
            localStorage.removeItem(STORAGE_KEYS.PORTAL);
        }
    } else {
        localStorage.removeItem(STORAGE_KEYS.PORTAL);
        sessionStorage.removeItem(STORAGE_KEYS.PORTAL);
    }
};

/**
 * Get auth token - DISABLED for security. Relying on cookies.
 */
export const getAuthToken = () => {
    return null;
};

/**
 * Set auth token - DISABLED for security.
 */
export const setAuthToken = (token, remember = false) => {
    // No-op for security
};

/**
 * Get API credentials from storage.
 */
export const getApiCredentials = () => {
    return {
        apiKey: sessionStorage.getItem(STORAGE_KEYS.API_KEY) || localStorage.getItem(STORAGE_KEYS.API_KEY),
        apiSecret: sessionStorage.getItem(STORAGE_KEYS.API_SECRET) || localStorage.getItem(STORAGE_KEYS.API_SECRET),
    };
};

/**
 * Set API credentials in storage (prefer client.setApiCredentials for axios wiring).
 */
export const setApiCredentials = (apiKey, apiSecret, remember = false) => {
    if (!apiKey || !apiSecret) return;
    const storage = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    storage.setItem(STORAGE_KEYS.API_KEY, apiKey);
    storage.setItem(STORAGE_KEYS.API_SECRET, apiSecret);
    other.removeItem(STORAGE_KEYS.API_KEY);
    other.removeItem(STORAGE_KEYS.API_SECRET);
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
    // Clear auth state and portal
    localStorage.removeItem(STORAGE_KEYS.IS_AUTHED);
    sessionStorage.removeItem(STORAGE_KEYS.IS_AUTHED);
    localStorage.removeItem(STORAGE_KEYS.PORTAL);
    sessionStorage.removeItem(STORAGE_KEYS.PORTAL);
    localStorage.removeItem('ds_lender_email');
    sessionStorage.removeItem('ds_lender_email');

    // Clear legacy tokens and keys
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.API_KEY);
    localStorage.removeItem(STORAGE_KEYS.API_SECRET);
    sessionStorage.removeItem(STORAGE_KEYS.API_KEY);
    sessionStorage.removeItem(STORAGE_KEYS.API_SECRET);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');

    // Clear CSRF token
    localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
};

/**
 * Get borrower email from storage
 */
export const getBorrowerEmail = () => {
    return (
        localStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
        localStorage.getItem('borrowerEmail') ||
        localStorage.getItem('email') ||
        localStorage.getItem('userEmail') ||
        sessionStorage.getItem(STORAGE_KEYS.BORROWER_EMAIL) ||
        sessionStorage.getItem('borrowerEmail') ||
        null
    );
};

/**
 * Set borrower email in storage
 */
export const setBorrowerEmail = (email, remember = false) => {
    if (email) {
        if (remember) {
            localStorage.setItem(STORAGE_KEYS.BORROWER_EMAIL, email);
        } else {
            sessionStorage.setItem(STORAGE_KEYS.BORROWER_EMAIL, email);
        }
    }
};

const authUtils = {
    isAuthenticated,
    getPortal,
    isAdminAuthed,
    isAuthedForPortal,
    setAuthState,
    setPortal,
    getAuthToken,
    setAuthToken,
    getApiCredentials,
    setApiCredentials,
    clearAuth,
    getBorrowerEmail,
    setBorrowerEmail,
};

export default authUtils;
